/**
 * MasterDataBankFormDialog — Create/Edit `bank`.
 * Compact Sales Contract style dialog. Single-column.
 *
 * `bank` là PK text: required, disable khi edit.
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdBankMutation,
  useUpdateMdBankMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataBank } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataBank | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  bank: string
  bank_name: string
  bank_name_eng: string
}

const EMPTY: FormState = {
  bank: '',
  bank_name: '',
  bank_name_eng: '',
}

export default function MasterDataBankFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createBank, { isLoading: creating }] = useCreateMdBankMutation()
  const [updateBank, { isLoading: updating }] = useUpdateMdBankMutation()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    if (isEdit && initialValue) {
      setForm({
        bank: initialValue.bank ?? '',
        bank_name: initialValue.bank_name ?? '',
        bank_name_eng: initialValue.bank_name_eng ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const codeEmpty = !form.bank.trim()
  const canSave = !codeEmpty && !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const buildBody = () => ({
    bank_name: form.bank_name.trim() || null,
    bank_name_eng: form.bank_name_eng.trim() || null,
  })

  const handleSave = async () => {
    if (codeEmpty) {
      setShowErrors(true)
      return
    }
    try {
      if (isEdit && initialValue) {
        await updateBank({ code: initialValue.bank, body: buildBody() }).unwrap()
        snack.success('Bank updated')
      } else {
        await createBank({ bank: form.bank.trim(), ...buildBody() }).unwrap()
        snack.success('Bank created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Bank' : 'New Bank'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.bank}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Bank Code"
              size="small"
              fullWidth
              required
              disabled={isEdit}
              error={showErrors && codeEmpty}
              value={form.bank}
              onChange={(e) => set('bank', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 1 }}
              helperText={isEdit ? 'Bank Code cannot be changed after creation.' : undefined}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Bank Name"
              size="small"
              fullWidth
              value={form.bank_name}
              onChange={(e) => set('bank_name', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Bank Name Eng"
              size="small"
              fullWidth
              value={form.bank_name_eng}
              onChange={(e) => set('bank_name_eng', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
